// src/pages/Farmer/VillagesList.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import farmerVisitService from '../../services/farmerVisit.service';
import Toast from '../../components/Toast/Toast';
import tourService from '../../services/tour.service';
import './FarmerVisitPages.css';

const VillagesList = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  // Determine visit type from route
  const isFollowup = location.pathname.includes('followup');
  const visitType  = isFollowup ? 'FOLLOWUP' : 'FIRST_VISIT';
  const pageTitle  = isFollowup ? 'Followup Visit' : 'First Visit';

  const [villages, setVillages]     = useState([]);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [todayTour, setTodayTour]   = useState(null);
  const [tourLoading, setTourLoading] = useState(true);
  const [toast, setToast]           = useState(null);

  // Check active tour
  useEffect(() => {
    const check = async () => {
      try {
        const res = await tourService.getTodayTour();
        if (res.success && res.data) setTodayTour(res.data);
      } catch (_) {}
      finally { setTourLoading(false); }
    };
    check();
  }, []);

  // Fetch villages
  useEffect(() => {
    fetchVillages();
  }, [search, visitType]);

  const fetchVillages = async () => {
    setLoading(true);
    try {
      const res = await farmerVisitService.getVillages(visitType, search);
      if (res.success && res.data) setVillages(res.data);
    } catch (_) {
      setToast({ message: 'Failed to fetch villages', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewFarmers = (village) => {
    const encoded = encodeURIComponent(village);
    if (isFollowup) {
      navigate(`/farmer/farmer-visit/village/${encoded}`);
    } else {
      navigate(`/farmer/first-visit/village/${encoded}`);
    }
  };

  if (tourLoading) {
    return (
      <div className="fv-container">
        <div className="fv-loading">Checking tour status...</div>
      </div>
    );
  }

  return (
    <div className="fv-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Breadcrumb */}
      <nav className="fv-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span>
        <span>Manage Farmer</span>
        <span>›</span>
        <span>{pageTitle}</span>
      </nav>

      {/* Header */}
      <div className="fv-header">
        <h4 className="fv-title">{pageTitle}</h4>
        <p className="fv-subtitle">Villages List | All Time</p>
      </div>

      {/* Tour check */}
      {!todayTour ? (
        <div className="fv-locked-card">
          <div className="fv-locked-icon">⚠️</div>
          <h5 className="fv-locked-title">No Tour Started Today</h5>
          <p className="fv-locked-text">You must start your tour before logging farmer visits.</p>
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button className="fv-btn-primary" onClick={() => navigate('/tour/start-km')}>
              Go to Start KM
            </button>
          </div>
        </div>
      ) : todayTour.status === 'COMPLETED' ? (
        <div className="fv-locked-card">
          <div className="fv-locked-icon">🔒</div>
          <h5 className="fv-locked-title">Tour Completed</h5>
          <p className="fv-locked-text">Your tour is already completed. No new visits can be logged.</p>
        </div>
      ) : todayTour.tourType !== 'WORKING' ? (
        <div className="fv-locked-card">
          <div className="fv-locked-icon">📋</div>
          <h5 className="fv-locked-title">
            {todayTour.tourType === 'LEAVE' ? 'Leave Day' : 'Campaigning Day'}
          </h5>
          <p className="fv-locked-text">Farmer visits cannot be logged today.</p>
        </div>
      ) : (
        <>
          {/* Active tour banner */}
          <div className="fv-tour-banner">
            <span className="fv-dot green" />
            Tour active — Start KM: <strong>{todayTour.startKm}</strong>
          </div>

          {/* Search */}
          <div className="fv-search-bar">
            <input
              type="text"
              className="fv-search-input"
              placeholder="Search for Villages.."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Table */}
          <div className="fv-table-container">
            {loading ? (
              <div className="fv-loading">Loading villages...</div>
            ) : (
              <table className="fv-table">
                <thead>
                  <tr>
                    <th>Sr.</th>
                    <th>Village</th>
                    <th>Farmers</th>
                  </tr>
                </thead>
                <tbody>
                  {villages.length > 0 ? (
                    villages.map((village, index) => (
                      <tr key={village}>
                        <td className="fv-sr">{index + 1}</td>
                        <td className="fv-village-name">{village}</td>
                        <td>
                          <button
                            className="fv-btn-view"
                            onClick={() => handleViewFarmers(village)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="fv-no-data">
                        {search ? `No villages found for "${search}"` : 'No villages available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VillagesList;